import { Menu } from '@headlessui/react'
import { MenuIcon } from '@heroicons/react/outline'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { Fragment, useMemo } from 'react'
import { useCookies } from 'react-cookie'
import Col from '../../components/basic/Col'
import AvatarImg from '../../components/common/AvatarImg'
import { BxGlobeIcon, FeArrowDown, FeArrowUp, FeatherChevronRight } from '../../components/common/Icons'
import LanguageToggle from '../../components/common/LanguageToggle'
import Span from '../../components/common/Span'
import { sendGTMEvent } from '../../helpers/gtmHelper'
import { useCurrentUser } from '../../modules/context/UserContext'
import useAppRouter from '../../modules/hooks/common/useAppRouter'
import useHandleLogout from '../../modules/hooks/student/auth/useHandleLogout'
import { default as LinkLocale } from '../../modules/i18n/LinkLocale'
import { s_edit_profile, s_menu } from '../../modules/i18n/strings/auto/wording'
import useTranslation from '../../modules/i18n/useTranslation'
import { DATA_REF_KEY } from '../../modules/ids/e2e/student'
import { STUDENT_BTN_ID } from '../../modules/ids/gtm/student'
import { RINGLE_LOGO_ROUND } from '../../modules/images'
import { LOCATION_ICON } from '../../modules/svg/svg'
import urls from '../../modules/urls'
import {
  convertPriceWithCountryCode,
  currencyFormat,
  formatNumberAsCurrency,
  getDday,
  getUserCity,
  isMulticampus,
} from '../../modules/utils'
import { COUNTRY_ALPHA2_CODE, PROMOTION_DUE_DATE, TEENS_SESSION_ROLE, USER_INFO_STATUS } from '../../modules/vars'
import useUserStore from '../../store/useUserStore'
import Button from '../_legacy/Button'
import { useBasicPopup } from '../basic/popup/BasicPopup'
import BandBannerContainer from '../common/BandBannerContainer'
import { useStudentMenu } from '../common/menu/menuUtils'
import StaticTooltip from '../common/tooltip/StaticTooltip'

type PortalMenuV2PropsType = {
  activeId: any
  currentUser: any
  pageName?: string
  pageDescription?: string
  noProfileMenu?: boolean
  noBanner?: boolean
}

const PortalMenuV2 = ({
  activeId,
  currentUser,
  pageName,
  pageDescription,
  noProfileMenu = false,
  noBanner,
}: PortalMenuV2PropsType) => {
  const { t } = useTranslation()
  const router = useRouter()
  const [cookies, setCookies] = useCookies(['locale', 'check_coupon'])
  const showBadge = currentUser && currentUser.discount_coupons.length > 0
  const studentMenu = useStudentMenu()
  const getStudentLeftMenu = () => {
    return studentMenu.portalV2.leftMenu
  }

  const getStudentMyPage = () => {
    if (isMulticampus(currentUser)) {
      return studentMenu.portalV2.mypageMulticampus
    } else {
      return studentMenu.portalV2.mypage
    }
  }

  const transformBtnName = (btn_name) => {
    switch (btn_name) {
      case '홈':
        return 'Home'
      case '1:1 수업':
        return '1:1 Lessons'
      case '튜터':
        return 'Tutors'
      case '콘텐츠':
        return 'Content'
      case '학습 활동':
        return 'Activities'
      default:
        return btn_name
    }
  }
  const handleOnClickMenuItem = (btn_name) => {
    sendGTMEvent(STUDENT_BTN_ID.CLICK_PORTAL_NAVIGATION_MENU, {
      page_location: router.asPath,
      btn_name: transformBtnName(btn_name),
      device_type: 'pc_web',
    })
  }
  return (
    <Fragment>
      {/* GNB SECTION */}
      <BandBannerContainer />

      <nav className="lg:max-w-1040 mx-auto px-20">
        <div
          id="navbarCollapse"
          className="row no-gutters relative flex h-[70px] flex-row items-center justify-between lg:justify-center"
        >
          <Menu>
            {({ open }) => (
              <>
                <Col clx="flex items-center" auto px0>
                  {/* 로고 */}
                  {isMulticampus(currentUser) ? (
                    <LinkLocale dataRef={DATA_REF_KEY.btn_ringle} href={urls.student.portal.lessons.scheduleEnter}>
                      <img src={RINGLE_LOGO_ROUND} className="mr-12 h-[32px]" alt="ringle_logo" />
                    </LinkLocale>
                  ) : (
                    <LinkLocale dataRef={DATA_REF_KEY.btn_ringle} href={urls.student.newPortal.home}>
                      <img src={RINGLE_LOGO_ROUND} className="mr-12 h-[32px]" alt="ringle_logo" />
                    </LinkLocale>
                  )}
                  {pageDescription && <div className="text-14 ml-[11px] text-gray-700">{pageDescription}</div>}
                  <div className="hidden px-12 text-gray-700 sm:block lg:hidden">
                    {getStudentLeftMenu()
                      .filter((e) => !e.none)
                      .map((item, i) => (item.id == activeId ? item.label : ''))}
                    {studentMenu.portalV2.rightMenu.map((item, i) => (item.id == activeId ? item.label : ''))}
                    {activeId == 'menu_mypage' && t(s_menu.portal.mypage)}
                  </div>
                </Col>
                <Col auto clx="lg:hidden">
                  <Menu.Button className="items-center rounded-md text-gray-700 focus:outline-none">
                    <span className="sr-only">Open menu</span>
                    <MenuIcon className="h-24 w-24" aria-hidden="true" />
                  </Menu.Button>
                  <Menu.Items className="relative">
                    {/*태블릿 뷰포트 메뉴 드롭다운 */}
                    <Menu.Item>
                      <TabletMenu />
                    </Menu.Item>
                  </Menu.Items>
                </Col>
              </>
            )}
          </Menu>

          {/* 왼쪽 메뉴 */}
          {
            <Col auto h70 clx="col-6 items-center justify-start flex pl-[20px] lgd:hidden">
              <ul className="flex flex-row space-x-[24px]">
                {getStudentLeftMenu()
                  .filter((e) => !e.none)
                  .map((item, i) => (
                    <Fragment key={i}>
                      <li className="">
                        <LinkLocale
                          href={item.href}
                          className={classNames(
                            'nav-link relative font-bold hover:text-purple-500',
                            { 'active text-purple-500': activeId == item.id },
                            { 'text-gray-700': activeId != item.id }
                          )}
                          dataRef={'nav_' + item.id}
                        >
                          <span onClick={() => handleOnClickMenuItem(item.label)}>{item.label}</span>
                          {item.id === 'menu_original' && (
                            <>
                              <div className="absolute hidden md:block" style={{ top: -18 }}></div>
                            </>
                          )}
                        </LinkLocale>
                      </li>
                    </Fragment>
                  ))}
              </ul>
            </Col>
          }
          <Col h70 clx="flex justify-end items-center px-0 lgd:hidden">
            <ul className="flex flex-row items-center">
              {/* 영문서 교정 / 이벤트 / 구매 / 친구초대 */}
              <MidMenuList currentUser={currentUser} />
              {/* DROPDOWN MENU(PROFILE) */}
              {noProfileMenu || (
                <Menu as="nav" className="relative">
                  {({ open }) => (
                    <>
                      <Menu.Button className="flex items-center focus:outline-none">
                        <a
                          data-ref={DATA_REF_KEY.btn_profile}
                          className="dropdown-toggle mobile-hidden relative flex cursor-pointer items-center justify-center lg:ml-24"
                        >
                          {currentUser?.image_url ? (
                            <div className="relative flex items-center">
                              <AvatarImg
                                size={33}
                                src={currentUser?.image_url}
                                imgClass={
                                  activeId == 'menu_mypage'
                                    ? 'selected-border'
                                    : currentUser?.by_parent
                                    ? 'border-black bg-white border-2'
                                    : 'border-purple-500 bg-white border-2'
                                }
                                className="mx-8"
                              />
                              {showBadge && (
                                <div
                                  style={{
                                    width: 16,
                                    height: 16,
                                    top: -5,
                                    right: 2,
                                  }}
                                  className="font-12 absolute flex items-center justify-center rounded-full bg-red-500 text-white"
                                >
                                  N
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="relative">
                              <DefaultAvatar
                                color={
                                  currentUser?.session_role == TEENS_SESSION_ROLE.PARENT
                                    ? '#2c3f58'
                                    : currentUser?.image_default?.color
                                }
                                initial={currentUser?.image_default?.initial}
                                name={currentUser?.image_default?.name}
                                clx="mr-12 mt-0"
                                isChild={!currentUser?.by_parent}
                                active={true}
                              />
                              {showBadge && !cookies.check_coupon && (
                                <div
                                  style={{
                                    width: 16,
                                    height: 16,
                                    top: -5,
                                    right: 2,
                                  }}
                                  className="font-12 absolute flex items-center justify-center rounded-full bg-red-500 text-white"
                                >
                                  N
                                </div>
                              )}
                            </div>
                          )}

                          <div
                            style={{ maxWidth: '60px' }}
                            className={classNames(
                              open ? 'text-purple-500' : 'text-gray-700',
                              'text-truncate text-h5 username truncate font-medium'
                            )}
                          >
                            {currentUser?.profile?.korean_name || currentUser?.profile?.name || currentUser?.name}
                            {t(s_menu.sir)}
                          </div>
                          {open ? (
                            <FeArrowUp size={12} className="ml-4 text-purple-500" />
                          ) : (
                            <FeArrowDown size={12} className="ml-4 text-black" />
                          )}
                        </a>
                      </Menu.Button>

                      <Menu.Items>
                        <Menu.Item>
                          {/* {({ active }) => ( */}
                          <li
                            className="nav-item dropdown z-9999 absolute flex items-center"
                            style={{ top: 52, left: -160 }}
                          >
                            <div
                              style={{
                                width: 320,
                                maxHeight: 'calc(100vh - 140px)',
                                overflow: 'auto',
                                boxShadow: '0px 1px 3px 0 rgba(22, 28, 45, 0.12)',
                              }}
                              className="py-lg-4 mt-[12px] rounded-lg border border-gray-200 bg-white p-16 text-left"
                            >
                              <RingleMenuBody
                                getStudentMyPage={getStudentMyPage}
                                showBadge={showBadge}
                                cookies={cookies}
                              />
                            </div>
                          </li>
                        </Menu.Item>
                      </Menu.Items>
                    </>
                  )}
                </Menu>
              )}
            </ul>
          </Col>
        </div>
      </nav>
    </Fragment>
  )
}
export default PortalMenuV2

type MenuBodyPropsType = {
  getStudentMyPage: any
  showBadge?: any
  cookies?: any
}

const RingleMenuBody = ({ getStudentMyPage, showBadge, cookies }: MenuBodyPropsType) => {
  const { currentUser } = useCurrentUser()

  const handleLogout = useHandleLogout()
  const { t, locale } = useTranslation()

  return (
    <>
      {currentUser?.is_ringle_member && (
        <div style={{ top: 14, right: '-180px' }} className="absolute rounded-[8px] bg-white p-[10px]">
          <div className="mb-[16px] text-[12px] font-medium">Only Ringle Member</div>
          <LanguageToggle currentLang={locale} />
          <div className="mt-[16px]">
            <LinkLocale href={urls.student.newPortal.home}>
              <Button borderGray300 px12 md primary py8 border1 fontSize12 flex justifyCenter itemsCenter textGray600>
                New Portal
              </Button>
            </LinkLocale>
          </div>
        </div>
      )}
      <LanguageAndTimezone />
      {!currentUser?.is_multicampus_member && (
        <>
          <ProfileBanner />
          <SubText text={t(s_menu.mypage.account)} />
        </>
      )}
      <AccountMenuList
        currentUser={currentUser}
        getStudentMyPage={getStudentMyPage}
        showBadge={showBadge}
        cookies={cookies}
      />
      {/* FAQ/공지사항/설치가이드 */}
      <CommonMenuList getStudentMyPage={getStudentMyPage} />
      <HR />

      {/* 로그아웃 */}
      <a onClick={handleLogout} className="dropdown-item text-h5 mx-0 mt-4 cursor-pointer px-0 hover:text-purple-500">
        {t(s_menu.mypage.sign_out)}
      </a>
    </>
  )
}

type MidMenuListPropsType = {
  currentUser: any
}

// 영문서 교정 / 이벤트 / 구매/ 친구초대
const MidMenuList = ({ currentUser }: MidMenuListPropsType) => {
  const { locale } = useTranslation()
  const studentMenu = useStudentMenu()
  const dday = getDday(PROMOTION_DUE_DATE)
  const currentGeo = useUserStore((state) => state.session.currentGeo)

  const promotionText = useMemo(() => {
    const countryCode = currentGeo?.country_code || COUNTRY_ALPHA2_CODE.KR
    const convertedCountryCode = countryCode == COUNTRY_ALPHA2_CODE.KR ? countryCode : COUNTRY_ALPHA2_CODE.US
    const convertedPrice = convertPriceWithCountryCode(
      50000,
      convertedCountryCode,
      countryCode !== COUNTRY_ALPHA2_CODE.KR
    )
    const convertedCurrency =
      countryCode === COUNTRY_ALPHA2_CODE.KR
        ? currencyFormat(convertedPrice)
        : formatNumberAsCurrency(convertedPrice, countryCode, locale)
    return convertedCurrency + 'P'
  }, [currentGeo?.country_code, locale])

  const router = useRouter()

  const handleOnClick = (e, id) => {
    if (id === 'menu_price') {
      if (router.asPath.includes('/portal/trial/complete')) {
        sendGTMEvent(STUDENT_BTN_ID.CLICK_GNB_PURCHASE, {
          btn_name: 'purchase',
          device_type: 'pc_web',
          custom_page_title: 'trial_complete',
        })
      } else if (router.asPath.includes('/portal/welcome')) {
        sendGTMEvent(STUDENT_BTN_ID.CLICK_GNB_PURCHASE, {
          btn_name: 'purchase',
          device_type: 'pc_web',
          custom_page_title: 'trial_welcome',
        })
      }
    }
  }
  return (
    <div className="flex items-center justify-between space-x-[20px]">
      {!currentUser?.is_multicampus_member &&
        studentMenu.portalV2.rightMenu.map((item, i) => (
          <Menu key={`${i}_student_menu`} as="div">
            {({ open }) => (
              <>
                <Menu.Button
                  className={`flex items-center justify-center rounded-full py-8 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2`}
                >
                  <span
                    className={classNames('text-h5 hover:text-purple-500 ', open ? 'text-purple-500' : 'text-gray-700')}
                  >
                    {item?.href ? (
                      <LinkLocale href={item.href} dataRef={item.id}>
                        <Span
                          className={classNames(`relative font-medium`)}
                          size={14}
                          onClick={(e) => handleOnClick(e, item?.id)}
                        >
                          {item.label}
                        </Span>
                      </LinkLocale>
                    ) : (
                      <Span className="font-weight-normal relative" size={14}>
                        {item.label}

                        {item.showToolTip && (
                          <StaticTooltip
                            text={promotionText}
                            bgDanger
                            isTop
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              zIndex: 10,
                              top: 8,
                              left: -5,
                            }}
                          />
                        )}
                      </Span>
                    )}
                  </span>
                </Menu.Button>
              </>
            )}
          </Menu>
        ))}
    </div>
  )
}

const ProfileBanner = () => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()

  return (
    <>
      {currentUser?.user_info ? (
        currentUser?.user_info.status == USER_INFO_STATUS.START && (
          <LinkLocale
            href={urls.student.portal.mypage.profile}
            className="my-12 flex w-full items-center rounded-md bg-gray-100 p-4 text-black"
          >
            <div className="text-h5 p-4" style={{ maxWidth: 240 }}>
              {t(
                s_edit_profile.haven_t_completed_your_profile_yet_please_complete_your_profile_for_more_tailored_class_
              )}
            </div>
            <div className="ml-auto">
              <FeatherChevronRight className="text-black" />
            </div>
          </LinkLocale>
        )
      ) : (
        <LinkLocale
          href={urls.student.portal.mypage.profile}
          className="flex items-center bg-purple-500 p-4 text-gray-100"
        >
          <div className="text-h5">
            {t(
              s_edit_profile.complete_your_profile_and_earn_10_000p_your_profile_will_help_your_tutor_to_tailor_your_lesson_
            )}
          </div>
          <div>
            <FeatherChevronRight className="text-gray-100" />
          </div>
        </LinkLocale>
      )}
    </>
  )
}

type AccountMenuListPropsType = {
  getStudentMyPage: any
  showBadge: any
  cookies: any
  currentUser: any
}

const AccountMenuList = ({ getStudentMyPage, showBadge, cookies, currentUser }: AccountMenuListPropsType) => {
  return (
    <>
      {/* 계정관리 */}
      <div className="flex flex-col">
        {getStudentMyPage()
          .filter((e) => e.floor == 1)
          .filter((e) => !e.none)
          .map((item, i) => (
            <div key={i} className="dropdown-item text-h5 mx-0 mt-0 mb-8 px-0 text-black">
              <LinkLocale href={item.href}>
                <div
                  className="flex items-center"
                  data-ref={item.id === 'menu_credit' ? DATA_REF_KEY.btn_credits : null}
                >
                  <span className="cursor-pointer">{item.label}</span>
                  {item.id == 'menu_coupon_box' && showBadge && (
                    <div
                      className="font-12 ml-2 flex items-center justify-center rounded-full bg-red-500 text-white"
                      style={{ width: 16, height: 16 }}
                    >
                      N
                    </div>
                  )}
                </div>
              </LinkLocale>
            </div>
          ))}
      </div>
      {currentUser?.is_multicampus_member || <HR />}
    </>
  )
}

type CommonMenuListPropsType = {
  getStudentMyPage: any
  clx?: any
}

// {/* FAQ/공지사항/설치가이드 */}
const CommonMenuList = ({ getStudentMyPage, clx }: CommonMenuListPropsType) => {
  return (
    <>
      <div className="flex flex-wrap">
        {getStudentMyPage()
          .filter((e) => e.floor == 2)
          .filter((e) => !e.none)
          .map((item, i) => (
            <LinkLocale
              key={i}
              className={classNames('dropdown-item text-h5 mx-0 mt-0 mb-4 min-w-full px-0')}
              href={item.href}
              target={item.id == 'menu_zoom' ? '_blank' : '_self'}
            >
              <span className={classNames(clx)}>{item.label}</span>
            </LinkLocale>
          ))}
      </div>
    </>
  )
}

// {/* 타임존 & 언어 */}
const TimezoneMenuList = ({ noTimezone }: { noTimezone: any }) => {
  const { t, locale } = useTranslation()
  const { currentUser } = useCurrentUser()

  return (
    <>
      <div className="text-12 mb-8 text-gray-700">{t(s_menu.mypage.timezone_language)}</div>
      <div className="mb-8 flex items-center justify-between">
        <div className="text-h5">{getUserCity(currentUser?.timezone)}</div>
        {noTimezone || (
          <LinkLocale href={urls.student.portal.mypage.timezone} className="text-12 capitalize text-gray-500">
            {t(s_menu.mypage.change)}
          </LinkLocale>
        )}
      </div>
      {noTimezone || <LanguageToggle noPadding koEnOnly currentLang={locale} />}
      <HR />
    </>
  )
}
const LanguageAndTimezone = () => {
  const { currentUser } = useCurrentUser()
  const router = useAppRouter()
  const basicPopup = useBasicPopup()
  const getLanguageTitle = (lang) => {
    switch (lang) {
      case 'ko':
        return '한국어'
      case 'en':
        return 'English'
      case 'zh_chs':
        return '中文（简体）'
      case 'zh_cht':
        return '中文（繁體）'
      case 'ja':
        return '日本語'
      case 'vi':
        return 'Tiếng Việt'
    }
  }
  const handleLanguageClick = () => {
    router.push(urls.student.portal.mypage.language)
  }
  return (
    <>
      <div className="mt-[3px] flex w-full flex-col items-start justify-start">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <BxGlobeIcon size={14} className="text-purple-500" />
            <div className="ml-[4px] text-[14px] font-medium text-purple-500">
              {getLanguageTitle(router.query.lang)}
            </div>
          </div>
          <Button
            onClick={handleLanguageClick}
            borderGray300
            px11
            py2
            border1
            fontSize12
            flex
            justifyCenter
            itemsCenter
            textGray600
          >
            Edit
          </Button>
        </div>
        <div className="mt-[12px] flex w-full flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <img src={LOCATION_ICON} className="ml-[1px] h-[14px] w-[11px]" />
            <div className="ml-[6px] text-[14px] font-medium text-purple-500">{currentUser?.timezone}</div>
          </div>
          <Button
            onClick={() => router.push(urls.student.portal.mypage.timezone)}
            borderGray300
            px11
            py2
            border1
            fontSize12
            flex
            justifyCenter
            itemsCenter
            textGray600
          >
            Edit
          </Button>
        </div>
      </div>
      <HR />
    </>
  )
}
const TabletMenu = () => {
  const { currentUser } = useCurrentUser()
  const studentMenu = useStudentMenu()
  const handleLogout = useHandleLogout()

  const showBadge = currentUser && currentUser.discount_coupons.length > 0

  const getStudentLeftMenu = () => {
    return studentMenu.portalV2.leftMenu
  }

  const getStudentMyPage = () => {
    if (isMulticampus(currentUser)) {
      return studentMenu.portalV2.mypageMulticampus
    } else {
      return studentMenu.portalV2.mypage
    }
  }
  const { t, locale } = useTranslation()
  const [cookies, setCookies] = useCookies<any>(['locale', 'check_coupon'])
  return (
    <li className="nav-item dropdown z-9999 absolute flex items-center" style={{ top: 32, right: 0 }}>
      <div
        style={{
          width: 190,
          maxHeight: 'calc(100vh - 140px)',
          overflow: 'auto',
          boxShadow: '0px 1px 3px 0 rgba(22, 28, 45, 0.12)',
        }}
        className="py-lg-4 rounded-lg border border-gray-200 bg-white p-16 text-left"
      >
        <div className="text-12 mb-8 text-gray-700">{t(s_menu.mypage.timezone_language)}</div>
        <div className="mb-8 flex items-center justify-between">
          <div className="text-h5">{getUserCity(currentUser?.timezone)}</div>
          <LinkLocale href={urls.student.portal.mypage.timezone} className="text-12 capitalize text-gray-500">
            {t(s_menu.mypage.change)}
          </LinkLocale>
        </div>
        <LanguageToggle koEnOnly currentLang={locale} />
        <HR />

        {/* 태블릿 뷰 - 좌측 포털메뉴 */}
        <div className="flex flex-wrap">
          {getStudentLeftMenu()
            .filter((e) => !e.none)
            .map((item, i) => (
              <LinkLocale
                key={i}
                className={classNames('dropdown-item text-h5 mx-0 mt-0 mb-4 min-w-full px-0 text-black')}
                href={item.href}
              >
                {item.label}
              </LinkLocale>
            ))}
        </div>

        <HR />
        {/* 오른쪽 메뉴(멀티캠퍼스 멤버가 아닐 경우 노출) */}
        {!currentUser?.is_multicampus_member && (
          <>
            <ProfileBanner />
            <HR />
          </>
        )}

        <SubText text={t(s_menu.mypage.account)} />
        <AccountMenuList
          currentUser={currentUser}
          getStudentMyPage={getStudentMyPage}
          showBadge={showBadge}
          cookies={cookies}
        />
        <CommonMenuList getStudentMyPage={getStudentMyPage} />
        <HR />
        <div className="flex flex-wrap">
          {getStudentMyPage()
            .filter((e) => e.floor === 3)
            .filter((e) => !e.none)
            .map((item, i) => (
              <LinkLocale
                key={i}
                className={classNames('dropdown-item text-h5 mx-0 mt-0 mb-4 min-w-full px-0 text-black')}
                href={item.href}
              >
                {item.label}
              </LinkLocale>
            ))}
        </div>

        <a onClick={handleLogout} className="dropdown-item text-h5 mx-0 mt-4 cursor-pointer px-0 hover:text-purple-500">
          {t(s_menu.mypage.sign_out)}
        </a>
      </div>
      {/* 태블릿 -좌측 메뉴를 마이메뉴 드롭다운에 표시 */}
    </li>
  )
}
//Local Common components
export const HR = () => {
  return <hr className="my-16" />
}

export const SubText = ({ text }: { text: any }) => {
  return <div className="text-h6 mb-[8px] text-gray-700">{text}</div>
}

type DefaultAvatarPropsType = {
  isChild?: boolean
  active?: boolean
  color: any
  initial: any
  name?: any
  clx?: any
}

const DefaultAvatar = ({ isChild, active, color, initial, name, clx }: DefaultAvatarPropsType) => {
  return (
    <div className={classNames(clx, 'flex cursor-pointer items-center gap-[12px]')}>
      <div
        style={{ background: `${color}` }}
        className={classNames(
          { 'border-2': active },
          { 'border-black': !isChild && active },
          { 'border-purple-500': isChild && active },
          'text-sBody flex h-[33px] w-[33px] items-center justify-center rounded-full text-white'
        )}
      >
        {initial}
      </div>
      {name && <div className={classNames(active && 'text-purple-500', 'text-h5 font-medium')}>{name}</div>}
    </div>
  )
}
